import axios from 'axios';
import { PAYMENT_ACTION_TYPES, baseURL } from '../actions/types'; 
// Assuming PAYMENT_ACTION_TYPES and baseURL are defined here

// Utility: Get token from localStorage
const getAuthToken = () => {
  try {
    const loginData = JSON.parse(localStorage.getItem('loginData') || '{}');
    const directToken = localStorage.getItem('token');
    return loginData.token || directToken || null;
  } catch (error) {
    console.error('Error retrieving auth token:', error);
    return null;
  }
};

// Get auth headers configuration
const getAuthHeaders = () => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  return {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  };
};

// Utility to ensure URL is correctly formatted
const normalizeUrl = (base, path) => {
    // Ensure base URL has a trailing slash
    const normalizedBase = base.endsWith('/') ? base : base + '/';
    // Ensure path does not have a leading slash (since base has one)
    const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
    
    return normalizedBase + normalizedPath;
};


// Enhanced error handler
const handleApiError = (error, dispatch, failType, shouldReject = true) => { // ADDED shouldReject
  console.error('API Error:', error);
  
  let errorMessage = 'An unexpected error occurred';
  
  if (error.response) {
    if (error.response.status === 404) {
      errorMessage = 'API endpoint not found. Please check if the backend service is running.';
    } else if (error.response.status === 401) {
      errorMessage = 'Authentication failed. Please login again.';
    } else if (error.response.status === 500) {
      errorMessage = error.response.data?.message || 
                    error.response.data?.error || 
                    'Server error. Please try again later.';
    } else {
      errorMessage = error.response.data?.message || 
                    error.response.data?.error || 
                    `Server error: ${error.response.status}`;
    }
  } else if (error.request) {
    errorMessage = 'No response from server. Please check your connection.';
  } else {
    errorMessage = error.message;
  }

  dispatch({
    type: failType,
    payload: errorMessage,
  });
  
  // Only reject the Promise if specifically told to (for create/update actions)
  if (shouldReject) {
      return Promise.reject(new Error(errorMessage));
  }
  return new Error(errorMessage); // Return error object without rejecting promise chain
};

// Generic API action creator for POST/PATCH
const createApiAction = (types, path, method = 'post', data = null) => async (dispatch) => {
  try {
    dispatch({ type: types.REQUEST });
    
    const url = normalizeUrl(baseURL, path); // Use normalizeUrl

    const config = getAuthHeaders();
    let response;

    switch (method.toLowerCase()) {
      case 'post':
        response = await axios.post(url, data, config);
        break;
      case 'patch':
        response = await axios.patch(url, data, config);
        break;
      default:
        throw new Error('Unsupported method for createApiAction');
    }

    const payload = response.data.success === 1 ? (response.data.data || response.data) : response.data;

    dispatch({
      type: types.SUCCESS,
      payload,
    });
    return Promise.resolve(payload);

  } catch (error) {
    // CRITICAL: Re-reject promise here to stop calling code (e.g., component's handleSubmit)
    return handleApiError(error, dispatch, types.FAIL, true); 
  }
};

// Generic GET action with graceful error handling
const createGetAllAction = (types, path) => async (dispatch, getState) => {
  try {
    dispatch({ type: types.REQUEST });

    const url = normalizeUrl(baseURL, path); // Use normalizeUrl

    const config = getAuthHeaders();
    const hospitalId = getState().login?.loginData?.hospitalID || JSON.parse(localStorage.getItem('loginData') || '{}').hospitalID || '0';

    const { data } = await axios.post( // Keep POST for now, assuming backend is built this way
      url,
      { hid: hospitalId },
      config
    );

    const settings = data.success === 1 ? (data.data || []) : [];

    dispatch({
      type: types.SUCCESS,
      payload: settings,
    });
    return Promise.resolve(settings);

  } catch (error) {
    // CRITICAL: Handle error but DO NOT re-reject the promise (shouldReject=false)
    handleApiError(error, dispatch, types.FAIL, false); 
    
    // Always resolve with an empty array to prevent crashing and clear loading state.
    dispatch({
      type: types.SUCCESS,
      payload: [],
    });
    return Promise.resolve([]);
  }
};


// ==================== RAZORPAY PAYMENT SETTINGS ====================
// CRITICAL: Ensure all URL paths are correct!
export const createRazorpaySetting = (razorpayData) => 
  createApiAction(
    {
      REQUEST: PAYMENT_ACTION_TYPES.CREATE_RAZORPAY_SETTING_REQUEST,
      SUCCESS: PAYMENT_ACTION_TYPES.CREATE_RAZORPAY_SETTING_SUCCESS,
      FAIL: PAYMENT_ACTION_TYPES.CREATE_RAZORPAY_SETTING_FAIL
    },
    'api/appointmentPaymentSettings/razorpay/create',
    'post',
    razorpayData
  );

export const getAllRazorpaySettings = (hid) => 
  createGetAllAction(
    {
      REQUEST: PAYMENT_ACTION_TYPES.GET_RAZORPAY_SETTINGS_REQUEST,
      SUCCESS: PAYMENT_ACTION_TYPES.GET_RAZORPAY_SETTINGS_SUCCESS,
      FAIL: PAYMENT_ACTION_TYPES.GET_RAZORPAY_SETTINGS_FAIL
    },
    'api/appointmentPaymentSettings/razorpay/getall'
  );

export const updateRazorpaySetting = (razorpayData) => 
  createApiAction(
    {
      REQUEST: PAYMENT_ACTION_TYPES.UPDATE_RAZORPAY_SETTING_REQUEST,
      SUCCESS: PAYMENT_ACTION_TYPES.UPDATE_RAZORPAY_SETTING_SUCCESS,
      FAIL: PAYMENT_ACTION_TYPES.UPDATE_RAZORPAY_SETTING_FAIL
    },
    'api/appointmentPaymentSettings/razorpay/update',
    'patch',
    razorpayData
  );

// ==================== CCAVENUE SETTINGS ====================
export const createCcavenueSetting = (ccavenueData) => 
  createApiAction(
    {
      REQUEST: PAYMENT_ACTION_TYPES.CREATE_CCAVENUE_SETTING_REQUEST,
      SUCCESS: PAYMENT_ACTION_TYPES.CREATE_CCAVENUE_SETTING_SUCCESS,
      FAIL: PAYMENT_ACTION_TYPES.CREATE_CCAVENUE_SETTING_FAIL
    },
    'api/appointmentPaymentSettings/ccavenue/create',
    'post',
    ccavenueData
  );

export const getAllCcavenueSettings = (hid) => 
  createGetAllAction(
    {
      REQUEST: PAYMENT_ACTION_TYPES.GET_CCAVENUE_SETTINGS_REQUEST,
      SUCCESS: PAYMENT_ACTION_TYPES.GET_CCAVENUE_SETTINGS_SUCCESS,
      FAIL: PAYMENT_ACTION_TYPES.GET_CCAVENUE_SETTINGS_FAIL
    },
    'api/appointmentPaymentSettings/ccavenue/getall'
  );

export const updateCcavenueSetting = (ccavenueData) => 
  createApiAction(
    {
      REQUEST: PAYMENT_ACTION_TYPES.UPDATE_CCAVENUE_SETTING_REQUEST,
      SUCCESS: PAYMENT_ACTION_TYPES.UPDATE_CCAVENUE_SETTING_SUCCESS,
      FAIL: PAYMENT_ACTION_TYPES.UPDATE_CCAVENUE_SETTING_FAIL
    },
    'api/appointmentPaymentSettings/ccavenue/update',
    'patch',
    ccavenueData
  );

// ==================== CASHFREE PAYMENT SETTINGS ====================
export const createCashfreeSetting = (cashfreeData) => 
  createApiAction(
    {
      REQUEST: PAYMENT_ACTION_TYPES.CREATE_CASHFREE_SETTING_REQUEST,
      SUCCESS: PAYMENT_ACTION_TYPES.CREATE_CASHFREE_SETTING_SUCCESS,
      FAIL: PAYMENT_ACTION_TYPES.CREATE_CASHFREE_SETTING_FAIL
    },
    'api/appointmentPaymentSettings/cashfreepayin/create',
    'post',
    cashfreeData
  );

export const getAllCashfreeSettings = (hid) => 
  createGetAllAction(
    {
      REQUEST: PAYMENT_ACTION_TYPES.GET_CASHFREE_SETTINGS_REQUEST,
      SUCCESS: PAYMENT_ACTION_TYPES.GET_CASHFREE_SETTINGS_SUCCESS,
      FAIL: PAYMENT_ACTION_TYPES.GET_CASHFREE_SETTINGS_FAIL
    },
    'api/appointmentPaymentSettings/cashfreepayin/getall'
  );

export const updateCashfreeSetting = (cashfreeData) => 
  createApiAction(
    {
      REQUEST: PAYMENT_ACTION_TYPES.UPDATE_CASHFREE_SETTING_REQUEST,
      SUCCESS: PAYMENT_ACTION_TYPES.UPDATE_CASHFREE_SETTING_SUCCESS,
      FAIL: PAYMENT_ACTION_TYPES.UPDATE_CASHFREE_SETTING_FAIL
    },
    'api/appointmentPaymentSettings/cashfreepayin/update',
    'patch',
    cashfreeData
  );

// ==================== CASHFREE PAYOUT SETTINGS ====================
export const createCashfreePayoutSetting = (cashfreePayoutData) => 
  createApiAction(
    {
      REQUEST: PAYMENT_ACTION_TYPES.CREATE_CASHFREE_PAYOUT_SETTING_REQUEST,
      SUCCESS: PAYMENT_ACTION_TYPES.CREATE_CASHFREE_PAYOUT_SETTING_SUCCESS,
      FAIL: PAYMENT_ACTION_TYPES.CREATE_CASHFREE_PAYOUT_SETTING_FAIL
    },
    'api/appointmentPaymentSettings/cashfreepayout/create',
    'post',
    cashfreePayoutData
  );

export const getAllCashfreePayoutSettings = (hid) => 
  createGetAllAction(
    {
      REQUEST: PAYMENT_ACTION_TYPES.GET_CASHFREE_PAYOUT_SETTINGS_REQUEST,
      SUCCESS: PAYMENT_ACTION_TYPES.GET_CASHFREE_PAYOUT_SETTINGS_SUCCESS,
      FAIL: PAYMENT_ACTION_TYPES.GET_CASHFREE_PAYOUT_SETTINGS_FAIL
    },
    'api/appointmentPaymentSettings/cashfreepayout/getall'
  );

export const updateCashfreePayoutSetting = (cashfreePayoutData) => 
  createApiAction(
    {
      REQUEST: PAYMENT_ACTION_TYPES.UPDATE_CASHFREE_PAYOUT_SETTING_REQUEST,
      SUCCESS: PAYMENT_ACTION_TYPES.UPDATE_CASHFREE_PAYOUT_SETTING_SUCCESS,
      FAIL: PAYMENT_ACTION_TYPES.UPDATE_CASHFREE_PAYOUT_SETTING_FAIL
    },
    'api/appointmentPaymentSettings/cashfreepayout/update',
    'patch',
    cashfreePayoutData
  );

// ==================== RAZORPAY PAYOUT SETTINGS ====================
export const createRazorpayPayoutSetting = (razorpayPayoutData) => 
  createApiAction(
    {
      REQUEST: PAYMENT_ACTION_TYPES.CREATE_RAZORPAY_PAYOUT_SETTING_REQUEST,
      SUCCESS: PAYMENT_ACTION_TYPES.CREATE_RAZORPAY_PAYOUT_SETTING_SUCCESS,
      FAIL: PAYMENT_ACTION_TYPES.CREATE_RAZORPAY_PAYOUT_SETTING_FAIL
    },
    '/api/appointmentPaymentSettings/razorpay/create', 
    'post',
    razorpayPayoutData
  );

export const getAllRazorpayPayoutSettings = (hid) => 
  createGetAllAction(
    {
      REQUEST: PAYMENT_ACTION_TYPES.GET_RAZORPAY_PAYOUT_SETTINGS_REQUEST,
      SUCCESS: PAYMENT_ACTION_TYPES.GET_RAZORPAY_PAYOUT_SETTINGS_SUCCESS,
      FAIL: PAYMENT_ACTION_TYPES.GET_RAZORPAY_PAYOUT_SETTINGS_FAIL
    },
    '/api/appointmentPaymentSettings/razorpay/getall'
  );

export const updateRazorpayPayoutSetting = (razorpayPayoutData) => 
  createApiAction(
    {
      REQUEST: PAYMENT_ACTION_TYPES.UPDATE_RAZORPAY_PAYOUT_SETTING_REQUEST,
      SUCCESS: PAYMENT_ACTION_TYPES.UPDATE_RAZORPAY_PAYOUT_SETTING_SUCCESS,
      FAIL: PAYMENT_ACTION_TYPES.UPDATE_RAZORPAY_PAYOUT_SETTING_FAIL
    },
    '/api/appointmentPaymentSettings/razorpay/update', 
    'patch',
    razorpayPayoutData
  );

// Reset Payment State
export const resetPaymentState = () => (dispatch) => {
  dispatch({ type: PAYMENT_ACTION_TYPES.RESET_PAYMENT_STATE });
};